import React from 'react';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

export const stopHasLocation = (s) => Boolean((s.facility && s.facility.trim()) || (s.city && s.city.trim()) || (s.address && s.address.trim()));

const SInput = ({ label, value, onChange, testid, invalid }) => (
  <div>
    <label className="block text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-0.5">{label}</label>
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className={`w-full text-xs border rounded px-2 py-1 ${invalid ? 'border-red-300' : 'border-slate-300'}`}
    />
  </div>
);

export const MultiLoadStopEditor = ({ stops, gi, onUpdate, onAdd, onDelete, onMove }) => (
  <div data-testid={`stop-editor-${gi}`}>
    <div className="flex items-center justify-between mb-2">
      <h4 className="text-xs font-bold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
        Stops in sequence — edit, reorder, add or remove
      </h4>
      <button
        type="button"
        onClick={() => onAdd(gi)}
        data-testid={`stop-add-${gi}`}
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#002FA7] text-white text-[11px] font-semibold hover:bg-[#00247D]"
      >
        + Add stop
      </button>
    </div>
    <div className="space-y-2">
      {stops.map((s, si) => {
        const missing = !stopHasLocation(s);
        return (
          <div
            key={si}
            data-testid={`stop-row-${gi}-${si}`}
            className={`rounded-md border p-2.5 bg-white ${missing ? 'border-red-300' : 'border-slate-200'}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 flex items-center justify-center rounded-full bg-slate-100 text-[11px] font-bold text-slate-700">{si + 1}</span>
              <select
                value={s.type || 'delivery'}
                onChange={(e) => onUpdate(gi, si, { type: e.target.value })}
                data-testid={`stop-type-${gi}-${si}`}
                className="text-xs border border-slate-300 rounded px-2 py-1 bg-white"
              >
                <option value="pickup">Pickup</option>
                <option value="delivery">Delivery</option>
              </select>
              <div className="ml-auto flex items-center gap-1">
                <button type="button" onClick={() => onMove(gi, si, -1)} disabled={si === 0} data-testid={`stop-up-${gi}-${si}`} className="px-1.5 py-1 rounded border border-slate-200 text-slate-600 text-xs disabled:opacity-30 hover:bg-slate-50">↑</button>
                <button type="button" onClick={() => onMove(gi, si, 1)} disabled={si === stops.length - 1} data-testid={`stop-down-${gi}-${si}`} className="px-1.5 py-1 rounded border border-slate-200 text-slate-600 text-xs disabled:opacity-30 hover:bg-slate-50">↓</button>
                <button type="button" onClick={() => onDelete(gi, si)} data-testid={`stop-delete-${gi}-${si}`} className="px-1.5 py-1 rounded border border-red-200 text-red-600 text-xs hover:bg-red-50">Delete</button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <SInput label="Facility / code" value={s.facility} onChange={(v) => onUpdate(gi, si, { facility: v })} testid={`stop-facility-${gi}-${si}`} invalid={missing} />
              <SInput label="Address" value={s.address} onChange={(v) => onUpdate(gi, si, { address: v })} testid={`stop-address-${gi}-${si}`} />
              <SInput label="City" value={s.city} onChange={(v) => onUpdate(gi, si, { city: v })} testid={`stop-city-${gi}-${si}`} />
              <div>
                <label className="block text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-0.5">State</label>
                <select
                  value={s.state || ''}
                  onChange={(e) => onUpdate(gi, si, { state: e.target.value })}
                  data-testid={`stop-state-${gi}-${si}`}
                  className="w-full text-xs border rounded px-2 py-1 bg-white border-slate-300"
                >
                  <option value="">—</option>
                  {US_STATES.map((st) => <option key={st} value={st}>{st}</option>)}
                </select>
              </div>
              <SInput label="ZIP" value={s.zip} onChange={(v) => onUpdate(gi, si, { zip: v })} testid={`stop-zip-${gi}-${si}`} />
              <div>
                <label className="block text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-0.5">Appt date</label>
                <input type="text" placeholder="MM/DD/YYYY" value={s.appointment_date || ''} onChange={(e) => onUpdate(gi, si, { appointment_date: e.target.value })} data-testid={`stop-date-${gi}-${si}`} className="w-full text-xs border border-slate-300 rounded px-2 py-1" />
              </div>
              <div>
                <label className="block text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-0.5">Appt time</label>
                <input type="text" placeholder="HH:MM" value={s.appointment_time || ''} onChange={(e) => onUpdate(gi, si, { appointment_time: e.target.value })} data-testid={`stop-time-${gi}-${si}`} className="w-full text-xs border border-slate-300 rounded px-2 py-1" />
              </div>
            </div>
            {missing && <p className="text-[10px] text-red-600 mt-1.5">Add a facility code or a city so this stop can be imported.</p>}
          </div>
        );
      })}
      {stops.length === 0 && (
        <p className="text-xs text-slate-500 italic">No stops detected. Click "Add stop" to create one.</p>
      )}
    </div>
  </div>
);
